import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export type DateRangePreset = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'year_to_date' | 'all_time' | 'custom';

export interface DateRangeValue {
  startDate: Date | null;
  endDate: Date | null;
  preset: DateRangePreset;
}

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  className?: string;
}

export function DateRangeSelector({ value, onChange, className }: DateRangeSelectorProps) {
  const presetOptions = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'year_to_date', label: 'Year to Date' },
    { value: 'all_time', label: 'All Time' }
  ];

  const calculateDateRange = (preset: DateRangePreset): { startDate: Date | null; endDate: Date | null } => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    switch (preset) {
      case 'last_7_days': {
        const start = new Date(today);
        start.setDate(today.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        return { startDate: start, endDate: today };
      }
      case 'last_30_days': {
        const start = new Date(today);
        start.setDate(today.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        return { startDate: start, endDate: today };
      }
      case 'last_90_days': {
        const start = new Date(today);
        start.setDate(today.getDate() - 89);
        start.setHours(0, 0, 0, 0);
        return { startDate: start, endDate: today };
      }
      case 'year_to_date': {
        const start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        return { startDate: start, endDate: today };
      }
      case 'all_time':
        return { startDate: null, endDate: null };
      default:
        return { startDate: null, endDate: null };
    }
  };

  const handlePresetChange = (preset: string) => {
    const presetValue = preset as DateRangePreset;
    const range = calculateDateRange(presetValue);
    onChange({
      ...range,
      preset: presetValue
    });
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-2 dark:text-white">
        Date Range
      </label>
      <Select
        value={value.preset}
        onValueChange={handlePresetChange}
      >
        <SelectTrigger className="w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white">
          <SelectValue placeholder="Select date range" />
        </SelectTrigger>
        <SelectContent>
          {presetOptions.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {value.startDate && value.endDate && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          {value.startDate.toLocaleDateString()} - {value.endDate.toLocaleDateString()}
        </div>
      )}
    </div>
  );
}